import { usePartDefinition } from "@project/emerge";
import { markProductSceneChanged } from "@project/emerge/src/new/sceneActivity";
import type { AbstractPartDefinition, Sides } from "@project/emerge-types";
import { useLayoutEffect, useRef } from "react";
import * as THREE from "three";
import { DoubleSide } from "three";
import type { MiterType } from "../enum/MiterType";
import type { LTPartType } from "../enum/PartType";
import {
  createRackRailGeometry,
  rackRailGeometryCache,
  rackRailGeometryInput,
  rackRailGeometryKey,
} from "../geometry/rackRailGeometry";
import type { RackRailType } from "../interfaces/RackRailType";
import { useLatestCommittedResource } from "./useLatestCommittedResource";

interface RackRailRendererProps
  extends AbstractPartDefinition<LTPartType.RackRail> {
  partSelection: RackRailType;
  points: [THREE.Vector3, THREE.Vector3];
  orientation: number;
  miterType: MiterType;
  cutReference: Sides;
}

export const RackRailRenderer = ({ id }: { id: string }) => {
  const partDef = usePartDefinition<RackRailRendererProps>({
    id,
    select: [
      "partSelection",
      "points",
      "orientation",
      "miterType",
      "cutReference",
      "position",
    ],
  });

  const meshRef = useRef<THREE.Mesh | null>(null);

  const input = rackRailGeometryInput(partDef);
  const geometryKey = input ? rackRailGeometryKey(input) : null;
  const geometry = useLatestCommittedResource(geometryKey, () => {
    if (!input || geometryKey === null) {
      throw new Error("Missing rack rail geometry inputs");
    }
    return rackRailGeometryCache.acquire(geometryKey, () =>
      createRackRailGeometry(input),
    );
  });
  const orientation = partDef?.orientation ?? 0;
  useLayoutEffect(() => {
    if (geometry && meshRef.current) markProductSceneChanged(meshRef.current);
  }, [geometry, orientation]);

  if (!geometry) return null;

  // React Three Fiber props that Biome doesn't recognize - these are valid props
  const groupRotationProps = {
    rotation: [Math.PI / 2, 0, orientation] as [number, number, number],
  };
  const materialProps = {
    color: "#404040",
    metalness: 0.3,
    roughness: 0.6,
    side: DoubleSide,
  };

  return (
    <group>
      <group {...groupRotationProps}>
        {/* A prop, not `<primitive attach>`: the cache owns this geometry and
            R3F never disposes a prop value. See `useLatestCommittedResource`. */}
        <mesh ref={meshRef} geometry={geometry}>
          <meshStandardMaterial {...materialProps} />
        </mesh>
      </group>
    </group>
  );
};
